import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { delay, distinctUntilChanged, filter, startWith, take } from 'rxjs/operators';
import { Ingredient } from 'src/app/shared/model';
import { getIngredients } from 'src/app/store/ingredients/ingredients.selector';

import {
  ActiveIngredientSetting,
  ActiveIngredientSettingDialogModel,
} from '../../activeingredientsetting.model';

@Component({
  selector: 'app-activeingredientsetting-dialog',
  templateUrl: './activeingredientsetting-dialog.component.html',
  standalone: false,
})
export class ActiveIngredientSettingsDialogComponent extends AbstractFormDialog<ActiveIngredientSetting> {
  data = inject<ActiveIngredientSettingDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        ActiveIngredientSettingsDialogComponent,
        AbstractDialogResultData<ActiveIngredientSetting>
      >
    >(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);

  currentYear = new Date().getFullYear();
  availableCropYears = Array.from({ length: this.currentYear + 2 - 2020 }, (_, k) => k + 2020);
  formGroup = this.fb.group({
    ingredientId: [
      {
        value: this.data.activeIngredientSetting.ingredientId,
        disabled: !this.isNewActiveIngredientSetting(),
      },
      [Validators.required],
    ],
    calendarYear: [
      this.data.activeIngredientSetting.calendarYear ?? this.currentYear,
      [Validators.required],
    ],
    malusPoints: [
      this.data.activeIngredientSetting.malusPoints,
      [Validators.required, Validators.maxLength(50)],
    ],
    includeInSum: [this.data.activeIngredientSetting.includeInSum ?? false],
    forbidden: [this.data.activeIngredientSetting.forbidden ?? false],
  });
  ingredients = toSignal(this.store.pipe(select(getIngredients), take(1)), {
    initialValue: [] as Ingredient[],
  });

  private readonly ingredientIdValue = toSignal(
    this.formGroup.controls.ingredientId.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      delay(100),
      filter((value) => typeof value === 'string'),
    ),
    { initialValue: '' },
  );

  filteredIngredients = computed(() => {
    const filterValue = (this.ingredientIdValue() || '').toString().toLowerCase();
    return this.ingredients()
      .filter((option) => option.name.toLowerCase().includes(filterValue))
      .sort((a, b) => (a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1));
  });

  isNewActiveIngredientSetting() {
    return !this.data.activeIngredientSetting.ingredientId;
  }

  confirm() {
    const result = {
      ...this.data.activeIngredientSetting,
      ...this.formGroup.getRawValue(),
    };
    result.ingredientName = this.findIngredient(result.ingredientId)?.name;
    result.forbidden = Boolean(result.forbidden);
    result.includeInSum = Boolean(result.includeInSum);
    if (this.formGroup.valid && result.ingredientId) {
      super.confirm(result);
    }
  }

  displayIngredient() {
    return (selectedIngredientId?: number) =>
      selectedIngredientId ? this.findIngredient(selectedIngredientId)?.name : '';
  }

  findIngredient(selectedIngredientId: number): Ingredient {
    return this.ingredients().find((ingredient) => ingredient.id === selectedIngredientId);
  }
}
